import { createApp } from 'vue'
import { createPinia } from 'pinia'
import router from './router'
import App from './App.vue'
import Toast from 'vue-toastification'
import { createI18n } from 'vue-i18n'

// Inicializar Firebase
import '@/services/firebase'
import { initializeFirebase } from '@/services/initFirebase'

// Estilos
import './assets/css/main.css'
import 'vue-toastification/dist/index.css'

// Configurações de internacionalização
import ptBR from '@/locales/pt-BR.json'
import en from '@/locales/en.json'

const i18n = createI18n({
  legacy: false,
  locale: 'pt-BR',
  fallbackLocale: 'en',
  messages: {
    'pt-BR': ptBR,
    'en': en
  }
})

// Configurações do Toast
const toastOptions = {
  position: 'top-right' as const,
  timeout: 5000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: 'button',
  icon: true,
  rtl: false,
  transition: 'Vue-Toastification__bounce',
  maxToasts: 5,
  newestOnTop: true
}

const app = createApp(App)

// Plugins
app.use(createPinia())
app.use(router)
app.use(i18n)
app.use(Toast, toastOptions)

// Propriedades globais
app.config.globalProperties.$filters = {
  currency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  },
  
  date(value: string | Date): string {
    const date = new Date(value)
    return new Intl.DateTimeFormat('pt-BR').format(date)
  },
  
  datetime(value: string | Date): string {
    const date = new Date(value)
    return new Intl.DateTimeFormat('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date)
  },
  
  capitalize(value: string): string {
    if (!value) return ''
    return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
  },
  
  truncate(value: string, length: number = 50): string {
    if (!value) return ''
    return value.length > length ? value.substring(0, length) + '...' : value
  }
}

// Diretivas globais
app.directive('focus', {
  mounted(el) {
    el.focus()
  }
})

app.directive('click-outside', {
  beforeMount(el, binding) {
    el.clickOutsideEvent = (event: Event) => {
      if (!(el === event.target || el.contains(event.target as Node))) {
        binding.value(event)
      }
    }
    document.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted(el) {
    document.removeEventListener('click', el.clickOutsideEvent)
  }
})

// Tratamento de erros global
app.config.errorHandler = (err, _, info) => {
  console.error('Erro global capturado:', err, info)
  // Aqui você pode enviar o erro para um serviço de monitoramento
}

// Performance monitoring
if (import.meta.env.DEV) {
  app.config.performance = true
}

app.mount('#app')

// Inicializar Firebase após montar a aplicação
initializeFirebase()

// Service Worker (para PWA)
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('SW registrado: ', registration)
      })
      .catch((registrationError) => {
        console.log('SW falhou: ', registrationError)
      })
  })
}